import React, { useState, useEffect, useMemo } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  trackPeopleUpdatePersonSuccess,
  trackPeopleUpdatePersonError,
  trackPeopleUploadAdditionalPhotosSuccess,
  trackPeopleUploadAdditionalPhotosError,
} from '../../../../analytics';

import { HeadingBtnGroup } from '../../../../components/custom';
import { Form, FloatingLabel } from '../../../../components/form';
import { Spinner, Button, Modal } from '../../../../components/general';
import { AssetPreviewModal } from '../../../../components/modals';
import { DraggerSimplified } from '../../../../components/video';

import { updateFace } from '../../../../services/api/faces';
import { uploadFile } from '../../../../services/uploadFileService';

import { useProjectStore } from '../../../../stores/project';

import { Person, File } from '../../../../types';
import { isEmpty, getFileImg } from '../../../../utils';

import PersonPhoto from './personPhoto';
import { validateForm } from './personValidations';

interface Props {
  show: boolean;
  people: Person[];
  openedPersonId: string | null;
  togglePersonModal: (id: string | null) => void;
  fetchPeopleData?: (checkPhotos?: boolean) => void;
}

const EditPersonModal: React.FC<Props> = (props) => {
  const {
    show,
    people,
    openedPersonId = '',
    togglePersonModal,
    fetchPeopleData = () => {},
  } = props;

  const { currentProject } = useProjectStore();

  const [personData, setPersonData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    instagram: '',
    customFieldData: '',
  });

  const [uploadingImgs, setUploadingImgs] = useState<Record<string, any>>({});

  const [showPhotoModal, setShowPhoto] = useState(false);
  const [openedPhoto, setOpenedPhoto] = useState<File | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [deletingPhotos, setDeletingPhotos] = useState<string[]>([]);

  const openedPerson: Person | null = useMemo(() => {
    const foundPerson = people.find(
      (person) => person.face.elementId === openedPersonId,
    );

    return foundPerson ?? null;
  }, [people, openedPersonId]);

  useEffect(() => {
    if (!!openedPerson && !isEmpty(openedPerson.face)) {
      setPersonData({
        name: openedPerson.face.name ?? '',
        email: openedPerson.face.email ?? '',
        phoneNumber: openedPerson.face.phoneNumber ?? '',
        instagram: openedPerson.face.instagram ?? '',
        customFieldData: openedPerson.face.customFieldData ?? '',
      });
    }
  }, [openedPersonId, openedPerson]);

  const filteredUploading = useMemo(() => {
    const imagesIds = openedPerson?.files?.map((file) => file.elementId) ?? [];

    const filteredUploading = Object.keys(uploadingImgs)
      .map((id) => uploadingImgs[id])
      .filter((img) => img && !imagesIds.includes(img.elementId));

    if (isEmpty(filteredUploading) && isUploading) {
      trackPeopleUploadAdditionalPhotosSuccess(
        Object.keys(uploadingImgs).length,
      );

      // clear uploading states
      setUploadingImgs({});
      setIsUploading(false);

      toast.success(
        <>
          All additional photos of <b>{openedPerson?.face.name}</b> are
          uploaded!
        </>,
        { toastId: 'people-photos-upload-success' },
      );
    }

    return filteredUploading;
  }, [isUploading, openedPerson, uploadingImgs]);

  if (!currentProject) return null;

  const isPublic = currentProject.isAssigneeAccess ?? false;

  const detailsFieldClass = !!currentProject.customFieldLabel
    ? 'w-full sm:w-1/3'
    : 'w-full sm:w-1/2';

  const mainPhoto = openedPerson?.files?.[0] ?? null;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setPersonData((prevData) => ({ ...prevData, [name]: value }));

    setErrors({});
  };

  const handleUploadImage = async (
    file: UploadFile,
    fileList: UploadFile[],
  ) => {
    try {
      setIsUploading(true);

      // set uploading images data
      const uploadingImagesTemp = { ...uploadingImgs };

      fileList.forEach((file) => {
        uploadingImagesTemp[file.uid] = { id: file.uid, name: file.name };
      });

      setUploadingImgs(uploadingImagesTemp);

      const parentElementId = openedPerson?.face.elementId;

      await uploadFile({
        file,
        eventId: currentProject.eventId,
        parentElementId,
        onProgress,
        isPublic,
      });

      fetchPeopleData(true);
    } catch (error) {
      console.error(error);
      trackPeopleUploadAdditionalPhotosError(file.name);
      toast.error('Something went wrong. Try again later');
    }
  };

  const togglePhotoModal = (photo: File | null) => {
    if (!!photo) {
      setShowPhoto(true);
      setOpenedPhoto(photo);
    } else {
      setShowPhoto(false);
      setTimeout(() => setOpenedPhoto(null), 500);
    }
  };

  const onProgress = (e: any) => {
    setUploadingImgs((previousImgs) => ({ ...previousImgs, [e.id]: e }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors: Record<string, string> = await validateForm(personData);

    if (!isEmpty(errors)) setErrors(errors);
    else {
      setIsSaving(true);

      try {
        await updateFace({
          eventId: currentProject.eventId,
          elementId: openedPersonId ?? '',
          personData,
          isPublic,
        });

        fetchPeopleData();

        trackPeopleUpdatePersonSuccess();

        toast.success(
          <div>
            <b>{personData.name}</b> details are updated
          </div>,
        );
      } catch (error) {
        console.error(error);
        trackPeopleUpdatePersonError();
        toast.error('Something went wrong. Try again later');
      }

      setIsSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Modal show={show} onHide={() => togglePersonModal(null)} size="lg">
        <Modal.Header className="flex-col-reverse sm:flex-row">
          <Modal.Title>Update person – {openedPerson?.face.name}</Modal.Title>

          <HeadingBtnGroup
            group={people}
            id={openedPersonId}
            setId={togglePersonModal}
          />
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={onSubmit} disabled={isSaving}>
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <img
                src={getFileImg(mainPhoto)}
                className={classNames(
                  'w-24 sm:w-44 aspect-square object-cover rounded-full',
                  { 'cursor-pointer': !!mainPhoto },
                )}
                alt={`${openedPerson?.face.name} Icon`}
                onClick={() => togglePhotoModal(mainPhoto)}
              />

              <div className="flex flex-col gap-3 w-full sm:w-[calc(100%_-_11rem_-_1.5rem)]">
                <h5>Details</h5>

                <div className="flex flex-col sm:flex-row gap-3">
                  <FloatingLabel
                    label="Full name"
                    name="name"
                    value={personData.name}
                    onChange={onChange}
                    isInvalid={!!errors.name}
                    errMsg={errors.name}
                    required
                    className="w-full sm:w-1/2"
                  />

                  <FloatingLabel
                    label="Email address"
                    name="email"
                    value={personData.email}
                    onChange={onChange}
                    isInvalid={!!errors.email}
                    errMsg={errors.email}
                    className="w-full sm:w-1/2"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <FloatingLabel
                    label="Phone Number"
                    name="phoneNumber"
                    value={personData.phoneNumber}
                    onChange={onChange}
                    className={detailsFieldClass}
                  />

                  <FloatingLabel
                    label="Instagram"
                    name="instagram"
                    value={personData.instagram}
                    onChange={onChange}
                    className={detailsFieldClass}
                  />

                  {currentProject.customFieldLabel && (
                    <FloatingLabel
                      label={currentProject.customFieldLabel}
                      name="customFieldData"
                      value={personData.customFieldData}
                      onChange={onChange}
                      className="w-full sm:w-1/3"
                    />
                  )}
                </div>
              </div>
            </div>

            {openedPerson?.shareLink ? (
              <div className="text-start">
                <b>Share link</b>:{' '}
                <Link to={openedPerson.shareLink} target="_blank">
                  {openedPerson.shareLink}
                </Link>
              </div>
            ) : (
              <React.Fragment>
                <div className="flex flex-col gap-3">
                  <h5>Additional photos (optional)</h5>

                  <div className="text-sm italic">
                    To increase AI accuracy we recommend uploading up to 3
                    photos of each attendee.
                  </div>

                  <DraggerSimplified
                    type="image"
                    heading="Photo upload (1 and more)"
                    isMultiple
                    onDrop={handleUploadImage}
                  />
                </div>

                {!!openedPerson?.files &&
                  (openedPerson.files.length > 1 ||
                    !isEmpty(filteredUploading)) && (
                    <div className="flex flex-col gap-3">
                      <h6>All uploaded photos ({openedPerson.files.length})</h6>

                      <div className="flex flex-wrap gap-2">
                        {openedPerson.files?.map((img) => (
                          <PersonPhoto
                            key={img.elementId}
                            img={img}
                            allowDelete
                            deletingPhotos={deletingPhotos}
                            setDeletingPhotos={setDeletingPhotos}
                            refetchData={fetchPeopleData}
                            isPublic={isPublic}
                          />
                        ))}

                        {filteredUploading.map((img) => (
                          <Spinner
                            key={img.elementId}
                            customSize="w-16 sm:w-20"
                            color="primary"
                          />
                        ))}
                      </div>
                    </div>
                  )}
              </React.Fragment>
            )}

            <Button
              disabled={!isEmpty(errors)}
              isSubmitBtn
              isProcessing={isSaving}
            >
              Save
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <AssetPreviewModal
        show={showPhotoModal}
        onHide={() => togglePhotoModal(null)}
        file={openedPhoto}
      />
    </React.Fragment>
  );
};

export default EditPersonModal;
